import { AetherEnvironment } from './aether-environment';
import { CertificationSeries } from './certification-series';
import { ListParamsStrict, ListSharedResourceScope, ParamValueCondition } from './list-params';
import { Asset, BasicAsset, BasicEnvironment, BasicOrder, BasicTest, User } from './shared';
import { Tenant } from './tenant';
import { Values } from './type-utils';

export interface RegistrationList {
  readonly count: number;
  readonly rows: Registration[];
}

export const RegistrationType = {
  Certification: 'certification',
  Training: 'training',
} as const;
export type RegistrationType = Values<typeof RegistrationType>;

export const RegistrationStatus = {
  Active: 'active',
  Cancelled: 'cancelled',
  Pending: 'pending',
} as const;
export type RegistrationStatus = Values<typeof RegistrationStatus>;

export interface RegistrationEvent extends BasicAsset {
  parents?: BasicAsset[];
  tests?: BasicTest[];
  ownerId?: number;
  deletedAt?: string;
}

export interface InternalRegistration {
  aetherEnvironment?: AetherEnvironment | null;
  readonly createdAt: string;
  readonly deletedAt: string | null;
  endTime: string;
  environment: BasicEnvironment | null;
  event: RegistrationEvent | null;
  readonly id: number;
  invited: boolean;
  inviteRetries: number;
  inviteCleaned: boolean;
  isProctorU: boolean;
  order: BasicOrder | null;
  proctorId: number | null;
  startTime: string;
  status: RegistrationStatus;
  tenant?: Tenant | null;
  type: RegistrationType;
  readonly updatedAt: string;
  user: User;
}

export interface Registration {
  readonly id?: number;
  aetherEnvironment?: AetherEnvironment | null;
  aetherEnvironmentId?: AetherEnvironment['id'];
  type?: RegistrationType;
  status?: RegistrationStatus;
  startTime?: string;
  endTime?: string;
  proctorId?: number;
  invited?: boolean;
  inviteRetries?: number;
  inviteCleaned?: boolean | string;
  altEmail?: string;
  isProctorU?: boolean;
  tenant?: Tenant | null;
  tenantId?: Tenant['id'];
  event?: RegistrationEvent;
  eventId?: number;
  certSeries?: Asset | CertificationSeries;
  certSeriesId?: number;
  environment?: BasicEnvironment;
  environmentId?: number;
  order?: BasicOrder;
  orderId?: number;
  user?: User;
  userId?: number;
  readonly createdAt?: string;
  readonly updatedAt?: string;
  readonly deletedAt?: string | null;
}

export interface InternalListRegistrationsParams extends Omit<ListParamsStrict, 'all'> {
  aetherEnvironment?: boolean;
  complete?: boolean;
  endTime?: ParamValueCondition;
  eventId?: number[];
  eventName?: string;
  id?: number[];
  orderId?: number;
  proctorId?: number[];
  invited?: boolean;
  inviteCleaned?: boolean;
  isProctorU?: boolean;
  retrySortOrder?: boolean;
  scope?: RegistrationListScope;
  startTime?: ParamValueCondition;
  status?: RegistrationStatus;
  tenant?: boolean;
  trainingId?: number[];
  type?: RegistrationType;
  userEmail?: string;
  userRegion?: string;
  userType?: string;
  userId?: number[];
  includeInstructors?: boolean;
  withCloudResource?: boolean;
}

export interface ListRegistrationsParams
  extends Pick<
    InternalListRegistrationsParams,
    'attributes' | 'complete' | 'endTime' | 'eventId' | 'eventName' | 'id' | 'page' | 'limit' | 'order' | 'sort' | 'startTime' | 'type'
  > {}

export interface CreateRegistrationRequestBody {
  aetherEnvironmentId?: AetherEnvironment['id'] | null;
  certSeriesId?: number | null;
  endTime: string;
  environmentId?: number | null;
  eventId: number | null;
  orderId?: number | null;
  proctorId?: number | null;
  invited?: boolean;
  inviteRetries?: number;
  isProctorU?: boolean;
  startTime: string;
  status: RegistrationStatus;
  tenantId?: Tenant['id'] | null;
  type: RegistrationType;
  userId: number;
}

export interface UpdateRegistrationRequestBody extends Partial<CreateRegistrationRequestBody> {
  deletedAt?: null;
  inviteCleaned?: boolean | string;
}

export interface RescheduleRegistrationRequestBody {
  endTime: string;
  eventId: number;
  startTime: string;
}

export const RegistrationListScope = {
  All: ListSharedResourceScope.All,
  Archived: ListSharedResourceScope.Archived,
  Owned: ListSharedResourceScope.Owned,
} as const;
export type RegistrationListScope = Values<typeof RegistrationListScope>;
